import type { IMenuItemSDKAction } from '@wix/editor-elements-corvid-utils';
import type {
  IMenuAnimation,
  IMenuItemProps,
  MenuAnimationNameType,
  MenuOrientationType,
} from '../../../Menu.types';
import { MenuAnimationName, MenuAnimationPhase } from '../../../constants';
import { useRef } from 'react';

export const createSDKAction = (
  item: IMenuItemProps['item'],
  selected: boolean,
  sdkAction?: IMenuItemSDKAction,
) =>
  sdkAction &&
  ((e: React.MouseEvent) => {
    sdkAction?.(e, {
      ...item,
      selected,
    });
  });

export const hasHorizontalOrientation = (
  itemRef: React.RefObject<HTMLDivElement>,
) => {
  if (!itemRef?.current) {
    return;
  }

  const orientation = getComputedStyle(itemRef.current).getPropertyValue(
    '--orientation',
  ) as MenuOrientationType;

  return orientation === 'horizontal';
};

export const useEventListeners = (
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  itemRef: React.RefObject<HTMLDivElement>,
  animationNameToConfig: Record<MenuAnimationNameType, IMenuAnimation>,
  getAnimationName: () => MenuAnimationNameType,
) => {
  const exitAnimationTimeoutIdRef: React.MutableRefObject<number | null> =
    useRef(null);

  const abortExitAnimation = () => {
    clearTimeout(exitAnimationTimeoutIdRef.current!);
    exitAnimationTimeoutIdRef.current = null;
  };

  const showDropdownMenu = () => {
    const isExitAnimationInProgress = exitAnimationTimeoutIdRef.current;

    if (isExitAnimationInProgress) {
      abortExitAnimation();
    }

    const animationName = getAnimationName();
    const menuAnimationConfig = animationNameToConfig[animationName];
    menuAnimationConfig.setAnimationState(MenuAnimationPhase.EnterActive);

    setIsOpen(true);
  };

  const hideDropdownMenu = () => {
    const animationName = getAnimationName();
    const menuAnimationConfig = animationNameToConfig[animationName];

    if (animationName === MenuAnimationName.None) {
      setIsOpen(false);
    } else {
      menuAnimationConfig.setAnimationState(MenuAnimationPhase.ExitActive);

      exitAnimationTimeoutIdRef.current = window.setTimeout(() => {
        menuAnimationConfig.setAnimationState(MenuAnimationPhase.ExitDone);
        setIsOpen(false);
      }, menuAnimationConfig.animationDuration);
    }
  };

  return {
    onMouseEnter: () => {
      hasHorizontalOrientation(itemRef) && showDropdownMenu();
    },
    onMouseLeave: () => {
      hasHorizontalOrientation(itemRef) && hideDropdownMenu();
    },
  };
};
